import '../Compatibility.css';
import { Typography, Grid, Button, Box, Radio, Dialog, Alert } from '@mui/material';
import { DialogTemplate } from '../../DialogTemplate';
import { ExampleDialog } from './Dialogs/ExampleDialog';
import { MoreDialog } from './Dialogs/MoreDialog';
import compatibility_image from '../../../Assets/images/compatibility_image_2.png';
import InfoIcon from '@mui/icons-material/Info';
import React, { useEffect, useCallback } from 'react';
import ReactGA from 'react-ga4';
import { clearAllBodyScrollLocks, enableBodyScroll } from 'body-scroll-lock';

export interface GeneralInformationProps {
    handleResult(nextStep: number): void
}

export const GeneralInformation = (props: GeneralInformationProps) => {
    const { handleResult } = props;
    const [dialogPreview, setDialogPreview] = React.useState(-1);

    const [questionSellRestrictedItems, setQuestionSellRestrictedItems] = React.useState<boolean | null>(null);

    const [showErrors, setShowErrors] = React.useState<boolean>(false);

    const handleQuestionChange = useCallback(
        (state, answer: boolean) => (e: React.MouseEvent<HTMLSpanElement>) => {
            state(answer);
        },
        [],
    )
    
    const triggerFunctionOnEnter = useCallback(
        (state, answer) => (e: React.KeyboardEvent<HTMLSpanElement>) => {
            if(e.key === "Enter") {
                state(answer);
            }
        },
        [],
    )

    const openDialog = useCallback(
        (dialog: number) => (e: React.MouseEvent<HTMLSpanElement>) => {
            setDialogPreview(dialog);
        },
        [],
    )
    
    const closeDialog = useCallback(
        () => {
            const targetElement = document.getElementById("popup");
            if(targetElement != null) {
                enableBodyScroll(targetElement);
            }
            setDialogPreview(-1);
        },
        [],
    )

    useEffect(() => {
        return () => {
            clearAllBodyScrollLocks();
        }
    }, []);
    
    const showResult = useCallback(
        () => {
            if(questionSellRestrictedItems == null) {
              setShowErrors(true);
              return;
            }
            if(questionSellRestrictedItems === true) {
                ReactGA.event({
                    category: 'User',
                    action: 'CompatChk - Compatible'
                });
                globalThis.scrollTo(0, 0);
                handleResult(1);
            } else {
                ReactGA.event({
                    category: 'User',
                    action: 'CompatChk - No Restricted Items'
                });
                globalThis.scrollTo(0, 0);
                handleResult(2);
            }
        },
        [handleResult, questionSellRestrictedItems],
    )

    const dialogScreen = (screen: number) => {
        switch (screen) {
            case 0: 
                return (
                    <DialogTemplate
                        handleClose={closeDialog}
                    >
                        <ExampleDialog cashProducts={true} tobaccoProducts={false}/>
                    </DialogTemplate>
                )
            case 1: 
                return (
                    <DialogTemplate
                        handleClose={closeDialog}
                    >
                        <ExampleDialog cashProducts={false} tobaccoProducts={true}/>
                    </DialogTemplate>
                )
            case 2:
                return (
                    <DialogTemplate
                        handleClose={closeDialog}
                    >
                        <MoreDialog pornographyProducts={true}/>
                    </DialogTemplate>
                )
        }
    }

    return (
        <div className='compatibility_general_container'>
            
            <Grid container>
                <Grid item xs={12} md={12} className="compatibility_general_title_container">
                    <Typography className="compatibility_general_title">
                        Check my eligibility
                    </Typography>
                    <Typography className="compatibility_general_text">
                        Use this checklist to confirm your business is eligible for Product Level Blocking.
                    </Typography>
                </Grid>
            </Grid>
            <Grid container>
                <Grid item xs={12} md={6}>
                    <div className='compatibility_question'>
                        <Typography className="compatibility_general_label">
                            Do you sell any of the below restricted items? *
                            <ul>
                                <li className="compatibility_item">Alcohol</li>
                                <li className="compatibility_item">Gambling services</li>
                                <li className="compatibility_item">
                                    Tobacco products
                                    <div className="compatibility_checkbox_info" role="button">
                                        <div 
                                            tabIndex={0} 
                                            onKeyDown={triggerFunctionOnEnter(setDialogPreview, 1)}
                                            onClick={openDialog(1)} 
                                            className="compatibility_checkbox_info_click"
                                        >
                                            <InfoIcon className="compatibility_checkbox_info_icon"/>
                                            <Typography className="compatibility_checkbox_info_text">Examples</Typography>
                                        </div>
                                    </div>
                                </li>
                                <li className="compatibility_item">
                                    Pornography
                                    <div className="compatibility_checkbox_info" role="button">
                                        <div 
                                            tabIndex={0} 
                                            onKeyDown={triggerFunctionOnEnter(setDialogPreview, 2)}
                                            onClick={openDialog(2)} 
                                            className="compatibility_checkbox_info_click"
                                        >
                                            <InfoIcon className="compatibility_checkbox_info_icon"/>
                                            <Typography className="compatibility_checkbox_info_text">More Information</Typography>
                                        </div>
                                    </div>
                                </li>
                                <li className="compatibility_item">
                                    Gift cards, Prepaid cards or e-vouchers that can be used to purchase alcohol, gambling services, tobacco or pornography
                                    <div className="compatibility_checkbox_info" role="button">
                                        <div 
                                            tabIndex={0} 
                                            onKeyDown={triggerFunctionOnEnter(setDialogPreview, 0)}
                                            onClick={openDialog(0)} 
                                            className="compatibility_checkbox_info_click"
                                        >
                                            <InfoIcon className="compatibility_checkbox_info_icon"/>
                                            <Typography className="compatibility_checkbox_info_text">Examples</Typography>
                                        </div>
                                    </div>
                                </li>
                             </ul>
                        </Typography>
                        <Grid container spacing={3} className='compatibility_checkboxs'>
                            <Grid item xs={6} md={6}>
                                <Box 
                                    onKeyDown={triggerFunctionOnEnter(setQuestionSellRestrictedItems, true)}
                                    onClick={handleQuestionChange(setQuestionSellRestrictedItems, true)}
                                    tabIndex={0}
                                    className={questionSellRestrictedItems === true ? "compatibility_checkbox_selected compatibility_general_checkbox_container" : "compatbility_checkbox_not_selected compatibility_general_checkbox_container"}
                                >
                                    <Radio className='compatibility_check_radio' tabIndex={-1} checked={questionSellRestrictedItems === true}/>
                                    <Typography className="compatibility_checkbox_label">
                                        Yes
                                    </Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={6} md={6}>
                                <Box 
                                    onKeyDown={triggerFunctionOnEnter(setQuestionSellRestrictedItems, false)}
                                    onClick={handleQuestionChange(setQuestionSellRestrictedItems, false)}
                                    tabIndex={0}
                                    className={questionSellRestrictedItems === false ? "compatibility_checkbox_selected compatibility_general_checkbox_container" : "compatbility_checkbox_not_selected compatibility_general_checkbox_container"}
                                >
                                    <Radio className='compatibility_check_radio' tabIndex={-1} checked={questionSellRestrictedItems === false}/>
                                    <Typography className="compatibility_checkbox_label">
                                        No
                                    </Typography>
                                </Box>
                            </Grid>
                        </Grid>
                        {showErrors && questionSellRestrictedItems === null && 
                            <Typography className="compatibility_check_error_text">
                                Please select your answer to the above question to proceed.
                            </Typography>
                        }
                    </div>


                    <Typography className="compatibility_hint">*indicates mandatory field</Typography>

                    {showErrors && 
                        <div className="compatibility_warning_container">
                            <Alert severity="error">
                                <Typography variant="body1" className="compatibility_warning_text">                          
                                    One or more of the questions have not been answered. Please select your answer on the fields indicated and click ‘check’ to proceed.
                                </Typography>
                            </Alert>
                        </div>
                    }

                    <Button
                        variant="primary"
                        className="compatibility_check_button"
                        onClick={showResult}
                    >
                        <p className="compatibility_check_button_text">
                            Check
                        </p>
                    </Button>
                </Grid>
                <Grid item xs={0} md={1}/>
                <Grid className="compatibility_image_container" item xs={12} md={5}>
                    <img 
                        src={compatibility_image}
                        className="compatibility_check_image"
                        alt=""/>
                </Grid>
            </Grid>
            <Dialog 
                fullWidth={true}
                maxWidth={"md"} 
                scroll={"paper"}
                open={dialogPreview >= 0}
                aria-describedby="scroll-dialog-description"
            >
                {dialogScreen(dialogPreview)}
            </Dialog>
        </div>
    )
}